import React from 'react';
import { BarChart2, ChevronDown, RefreshCcw } from 'lucide-react';

// Dataset search/picker header for the Annotation tool. Presentational only —
// all state lives in AnnotationPage.
const DatasetSelector = ({
  datasetDropdownRef,
  datasetBadgeText,
  datasets,
  filteredDatasets,
  selectedDataset,
  datasetSearch,
  setDatasetSearch,
  setSelectedDataset,
  isDatasetDropdownOpen,
  setIsDatasetDropdownOpen,
  isLoadingDatasets,
  isRefreshingDatasets,
  datasetLoadError,
  onRefreshDatasets,
  onSelectDataset,
  lotsCount,
}) => (
  <div className="flex flex-col gap-3 mb-6 sm:mb-8">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Annotation tool</h1>
      <div className="flex items-center px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded border border-blue-100">
        <BarChart2 size={18} className="mr-2 flex-shrink-0" />
        <span className="text-xs sm:text-sm font-medium">
          Dataset: {datasetBadgeText}
        </span>
      </div>
    </div>
    <div className="flex flex-col md:flex-row md:items-stretch md:flex-nowrap gap-2">
      <div ref={datasetDropdownRef} className="relative md:flex-[2.4]">
        <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 h-full">
          <span className="text-sm font-medium text-slate-700 mr-2">Dataset:</span>
          <input
            value={datasetSearch}
            onChange={(e) => {
              const nextValue = e.target.value;
              const normalized = nextValue.trim().toLowerCase();
              const exactMatch = datasets.find((dataset) => {
                const datasetName = (dataset?.name || '').trim().toLowerCase();
                const datasetPrefix = (dataset?.prefix || '').trim().toLowerCase();
                return normalized && (datasetName === normalized || datasetPrefix === normalized);
              }) || null;
              setDatasetSearch(nextValue);
              setSelectedDataset(exactMatch);
              setIsDatasetDropdownOpen(true);
            }}
            onFocus={() => setIsDatasetDropdownOpen(true)}
            disabled={isLoadingDatasets}
            placeholder={isLoadingDatasets ? 'Loading datasets...' : 'Search datasets...'}
            className={`w-full bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium ${
              isLoadingDatasets ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          />
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setIsDatasetDropdownOpen((previous) => !previous)}
            className="ml-2 inline-flex items-center justify-center rounded border border-slate-300 bg-white px-1.5 py-1 text-slate-600 hover:bg-slate-100"
            title={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
            aria-label={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
          >
            <ChevronDown size={14} className={`transition-transform ${isDatasetDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onRefreshDatasets()}
            disabled={isRefreshingDatasets}
            className={`ml-1 inline-flex items-center justify-center rounded border px-1.5 py-1 transition-colors ${
              isRefreshingDatasets
                ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
            title="Repull dataset list from blob storage"
            aria-label="Repull dataset list from blob storage"
          >
            <RefreshCcw size={14} className={isRefreshingDatasets ? 'animate-spin' : ''} />
          </button>
        </div>
        {isDatasetDropdownOpen && (
          <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
            {isLoadingDatasets ? (
              <div className="px-3 py-2 text-xs text-slate-500">Loading datasets...</div>
            ) : filteredDatasets.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">
                {datasetLoadError
                  ? `Failed to load datasets: ${datasetLoadError}`
                  : (datasets.length > 0 ? 'No matching datasets.' : 'No datasets available.')}
              </div>
            ) : (
              filteredDatasets.map((dataset) => (
                <button
                  key={dataset.id || dataset.prefix || '__root__'}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelectDataset(dataset);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                    (selectedDataset?.prefix || '') === (dataset.prefix || '') ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                  }`}
                >
                  <div className="font-semibold">{dataset.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {(dataset.prefix || '__root__')} • {dataset.data_points || 0} data points • {dataset.image_count || 0} images
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <div className="flex items-center text-xs text-slate-500 md:ml-auto px-2">
        {selectedDataset ? <span>{lotsCount} lots</span> : null}
      </div>
    </div>
  </div>
);

export default DatasetSelector;
